/*
 * The Autofill review: what the case documents say, compared field by field
 * with the profile. New values are preselected, values that disagree with the
 * profile (or with another document) never are, and values that already match
 * are shown but can't be applied. Pure, so it is unit-tested directly.
 */
import type { ForeignNational } from "@/api/types/foreignNational";
import type {
  AutofillApplyRequest,
  AutofillField,
  AutofillFieldValue,
  AutofillHistoryKind,
  AutofillHistoryValues,
  AutofillProposals,
  AutofillSource
} from "@/api/types/foreignNationalRecords";
import { matchCountry } from "@/components/form";

import { formatShortDate } from "../../header/clientHeaderModel";

export type ReviewState = "new" | "conflict" | "match";

export type ReviewField = {
  id: string;
  field: AutofillField;
  label: string;
  value: AutofillFieldValue;
  display: string;
  current: string;
  state: ReviewState;
  /** Why it isn't preselected ("Documents disagree"). */
  note?: string;
  source: AutofillSource;
};

export type ReviewRow = {
  id: string;
  kind: AutofillHistoryKind;
  title: string;
  detail: string;
  values: AutofillHistoryValues;
  source: AutofillSource;
  existingId: string | null;
  state: "new" | "update" | "match";
  /** A date the document doesn't give but the row needs. */
  needs: { key: string; label: string } | null;
};

export type ReviewGroup = {
  key: string;
  label: string;
  title: string;
  caseLabel: string;
  fields: ReviewField[];
  rows: ReviewRow[];
};

export const FIELD_LABELS: Record<AutofillField, string> = {
  first_name: "First name",
  middle_name: "Middle name",
  last_name: "Last name",
  birthday: "Date of birth",
  city_of_birth: "City of birth",
  country_of_birth: "Country of birth",
  citizenships: "Citizenship",
  current_immigration_status: "Current status",
  immigration_status_expiration: "Status valid until",
  duration_of_status: "Duration of status (D/S)"
};

const DATE_FIELDS = new Set<AutofillField>([
  "birthday",
  "immigration_status_expiration"
]);

const text = (v: unknown): string => (typeof v === "string" ? v.trim() : "");
const day = (v: unknown): string => text(v).slice(0, 10);
const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

const currentOf = (client: ForeignNational, field: AutofillField): string => {
  if (field === "citizenships")
    return (
      (client.citizenships ?? []).join(", ") ||
      text(client.country_of_citizenship)
    );
  if (field === "duration_of_status")
    return client.duration_of_status ? "Yes" : "";
  const raw = (client as Record<string, unknown>)[field];
  return DATE_FIELDS.has(field) ? day(raw) : text(raw);
};

const showDate = (iso: string): string => formatShortDate(iso) || iso;

type Normalized = { value: AutofillFieldValue; display: string; key: string };

/** The value as the profile would store it, or null when it can't be used. */
const normalize = (
  client: ForeignNational,
  field: AutofillField,
  value: AutofillFieldValue
): Normalized | null => {
  if (field === "duration_of_status")
    return value ? { value: true, display: "Yes", key: "yes" } : null;
  if (field === "citizenships") {
    const country = matchCountry(Array.isArray(value) ? value[0] : text(value));
    if (!country) return null;
    const held = client.citizenships?.length
      ? client.citizenships
      : [text(client.country_of_citizenship)].filter(Boolean);
    const list = held.some((c) => same(c, country)) ? held : [...held, country];
    return { value: list, display: country, key: country.toLowerCase() };
  }
  if (field === "country_of_birth") {
    const country = matchCountry(text(value));
    return country
      ? { value: country, display: country, key: country.toLowerCase() }
      : null;
  }
  if (DATE_FIELDS.has(field)) {
    const d = day(value);
    return /^\d{4}-\d{2}-\d{2}$/.test(d)
      ? { value: d, display: showDate(d), key: d }
      : null;
  }
  const t = text(value);
  return t ? { value: t, display: t, key: t.toLowerCase() } : null;
};

const sourceKey = (s: AutofillSource): string =>
  s.case_document_id || `${s.label ?? "Document"}:${s.individual_id ?? ""}`;

const passportDetail = (v: AutofillHistoryValues): string =>
  [
    matchCountry(text(v.issuing_country)) ?? text(v.issuing_country),
    v.expiration_date ? `expires ${showDate(day(v.expiration_date))}` : ""
  ]
    .filter(Boolean)
    .join(" · ");

const yearOrDay = (iso: unknown, approximate: boolean): string => {
  const d = day(iso);
  if (!d) return "";
  return approximate ? d.slice(0, d.endsWith("-01-01") ? 4 : 7) : showDate(d);
};

/** "2018 – 2020", "Jun 2023 – present", keeping a CV's month or year precision. */
const periodOf = (v: AutofillHistoryValues, current: boolean): string => {
  const approx = text(v.approximate);
  const from = yearOrDay(v.start_date, approx.includes("start_date"));
  const to = current
    ? "present"
    : yearOrDay(v.end_date, approx.includes("end_date"));
  if (!from && !to) return "";
  return `${from || "?"} – ${to || "?"}`;
};

const backgroundRow = (
  h: AutofillProposals["history"][number],
  index: number
): ReviewRow | null => {
  const v = h.values;
  const base = {
    id: `row-${index}`,
    kind: h.kind,
    values: v,
    source: h.source,
    existingId: h.existing_id ?? null,
    state: h.existing_id ? ("match" as const) : ("new" as const)
  };
  const place = (...parts: unknown[]) =>
    parts.map(text).filter(Boolean).join(", ");
  if (h.kind === "employment") {
    return {
      ...base,
      title: [text(v.job_title), text(v.employer)].filter(Boolean).join(" at "),
      detail: [periodOf(v, v.is_current === true), place(v.city, v.country)]
        .filter(Boolean)
        .join(" · "),
      needs: v.start_date ? null : { key: "start_date", label: "Start date" }
    };
  }
  if (h.kind === "address") {
    return {
      ...base,
      title: place(v.street, v.city) || "Address",
      detail: [place(v.state, v.zip_code, v.country), periodOf(v, false)]
        .filter(Boolean)
        .join(" · "),
      needs: v.start_date ? null : { key: "start_date", label: "Moved in" }
    };
  }
  if (h.kind === "education") {
    return {
      ...base,
      title:
        [text(v.degree), text(v.field_of_study)].filter(Boolean).join(", ") ||
        "Degree",
      detail: [text(v.institution), periodOf(v, false)]
        .filter(Boolean)
        .join(" · "),
      needs: null
    };
  }
  return null;
};

const rowFor = (
  h: AutofillProposals["history"][number],
  index: number
): ReviewRow => {
  const background = backgroundRow(h, index);
  if (background) return background;
  const v = h.values;
  const existingId = h.existing_id ?? null;
  if (h.kind === "passport") {
    return {
      id: `row-${index}`,
      kind: h.kind,
      title: `Passport ${text(v.passport_number)}`.trim(),
      detail: passportDetail(v),
      values: {
        ...v,
        issuing_country:
          matchCountry(text(v.issuing_country)) ?? text(v.issuing_country)
      },
      source: h.source,
      existingId,
      state: h.matches ? "match" : existingId ? "update" : "new",
      needs: null
    };
  }
  if (h.kind === "status") {
    const range = [
      v.validity_start_date ? showDate(day(v.validity_start_date)) : "?",
      v.validity_expiration_date
        ? showDate(day(v.validity_expiration_date))
        : "D/S"
    ].join(" – ");
    return {
      id: `row-${index}`,
      kind: h.kind,
      title: `${text(v.status) || "Status"} status`,
      detail: [range, text(v.receipt_number)].filter(Boolean).join(" · "),
      values: v,
      source: h.source,
      existingId,
      state: existingId ? "match" : "new",
      needs: v.validity_start_date
        ? null
        : { key: "validity_start_date", label: "Valid from" }
    };
  }
  return {
    id: `row-${index}`,
    kind: h.kind,
    title: `I-94 admission${
      v.i94_record_number ? ` ${text(v.i94_record_number)}` : ""
    }`,
    detail: [
      text(v.class_of_admission),
      v.duration_of_status
        ? "admitted for D/S"
        : v.i94_expiration_date
        ? `admit until ${showDate(day(v.i94_expiration_date))}`
        : ""
    ]
      .filter(Boolean)
      .join(" · "),
    values: v,
    source: h.source,
    existingId,
    state: existingId ? "match" : "new",
    needs: v.event_date ? null : { key: "event_date", label: "Date of entry" }
  };
};

/** The review, grouped by the document each value came from. */
export const buildReview = (
  client: ForeignNational,
  proposals: AutofillProposals
): ReviewGroup[] => {
  const normalized = proposals.fields
    .map((p) => ({ p, n: normalize(client, p.field, p.value) }))
    .filter((x): x is { p: (typeof x)["p"]; n: Normalized } => x.n !== null);

  const valuesByField = new Map<AutofillField, Set<string>>();
  for (const { p, n } of normalized) {
    const set = valuesByField.get(p.field) ?? new Set<string>();
    set.add(n.key);
    valuesByField.set(p.field, set);
  }

  const groups = new Map<string, ReviewGroup>();
  const group = (s: AutofillSource, caseLabel: string): ReviewGroup => {
    const key = sourceKey(s);
    let g = groups.get(key);
    if (!g) {
      g = {
        key,
        label: s.label || "Document",
        title: text(s.document_title),
        caseLabel,
        fields: [],
        rows: []
      };
      groups.set(key, g);
    }
    return g;
  };

  const seen = new Set<string>();
  normalized.forEach(({ p, n }, i) => {
    const dedupe = `${sourceKey(p.source)}|${p.field}|${n.key}`;
    if (seen.has(dedupe)) return;
    seen.add(dedupe);
    const current = currentOf(client, p.field);
    const currentKey = DATE_FIELDS.has(p.field)
      ? current
      : current.toLowerCase();
    const matches =
      p.field === "citizenships"
        ? (client.citizenships ?? [client.country_of_citizenship ?? ""]).some(
            (c) => same(text(c), n.display)
          )
        : current !== "" && currentKey === n.key;
    const disagree = (valuesByField.get(p.field)?.size ?? 0) > 1;
    const state: ReviewState = matches
      ? "match"
      : current === "" && !disagree
      ? "new"
      : "conflict";
    group(p.source, p.case_label).fields.push({
      id: `field-${i}`,
      field: p.field,
      label: FIELD_LABELS[p.field],
      value: n.value,
      display: n.display,
      current:
        DATE_FIELDS.has(p.field) && current ? showDate(current) : current,
      state,
      note:
        state === "conflict"
          ? disagree
            ? "Documents disagree"
            : p.field === "citizenships"
            ? "Adds a citizenship"
            : undefined
          : undefined,
      source: p.source
    });
  });

  proposals.history.forEach((h, i) => {
    group(h.source, h.case_label).rows.push(rowFor(h, i));
  });

  return [...groups.values()].filter((g) => g.fields.length || g.rows.length);
};

/** What starts ticked: every new value, nothing that disagrees or matches. */
export const defaultSelection = (groups: ReviewGroup[]): Set<string> => {
  const out = new Set<string>();
  for (const g of groups) {
    for (const f of g.fields) if (f.state === "new") out.add(f.id);
    for (const r of g.rows) if (r.state !== "match" && !r.needs) out.add(r.id);
  }
  return out;
};

export const countActionable = (groups: ReviewGroup[]): number =>
  groups.reduce(
    (n, g) =>
      n +
      g.fields.filter((f) => f.state !== "match").length +
      g.rows.filter((r) => r.state !== "match").length,
    0
  );

/**
 * The request for the ticked items. A field ticked in two documents keeps
 * the last one; a row's missing date comes from `dates`.
 */
export const buildApplyRequest = (
  groups: ReviewGroup[],
  selected: ReadonlySet<string>,
  dates: Readonly<Record<string, string>>
): AutofillApplyRequest => {
  const fields = new Map<
    AutofillField,
    AutofillApplyRequest["fields"][number]
  >();
  const history: AutofillApplyRequest["history"] = [];
  const picked = (id: string, state: string) =>
    state !== "match" && selected.has(id);
  for (const g of groups) {
    for (const f of g.fields.filter((x) => picked(x.id, x.state))) {
      fields.set(f.field, { field: f.field, value: f.value, source: f.source });
    }
    for (const r of g.rows.filter((x) => picked(x.id, x.state))) {
      const d = r.needs ? (dates[r.id] ?? "").trim() : "";
      if (!r.needs || d) {
        history.push({
          kind: r.kind,
          values: r.needs ? { ...r.values, [r.needs.key]: d } : r.values,
          source: r.source,
          existing_id: r.existingId
        });
      }
    }
  }
  return { fields: [...fields.values()], history };
};

/** Ticked rows still waiting on a date the document doesn't give. */
export const missingDates = (
  groups: ReviewGroup[],
  selected: ReadonlySet<string>,
  dates: Readonly<Record<string, string>>
): ReviewRow[] =>
  groups
    .flatMap((g) => g.rows)
    .filter(
      (r) => r.needs && selected.has(r.id) && !(dates[r.id] ?? "").trim()
    );
